"use client"

import * as React from "react"
import { Link } from "react-router"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { STATUS_BADGE_TONE_CLASS } from "@/lib/list-status-badges"
import type { EvaluationCounts } from "@/lib/mock/evaluation-dashboard"

type PipelineStage = {
  id: string
  label: string
  count: number
  colorVar: string
}

export const STAGE_META = {
  setup: { label: "Setup", colorVar: "var(--chart-4)" },
  scheduled: { label: "Scheduled", colorVar: "var(--primary)" },
  live: { label: "Live", colorVar: "var(--brand-color)" },
  closed: { label: "Closed", colorVar: "var(--chart-2)" },
  published: { label: "Published", colorVar: "var(--chart-3)" },
} satisfies Record<string, { label: string; colorVar: string }>

function buildStages(counts: EvaluationCounts): PipelineStage[] {
  const setup = counts.notSetup + counts.draft
  const stages: PipelineStage[] = [
    { id: "setup", ...STAGE_META.setup, count: setup },
    { id: "scheduled", ...STAGE_META.scheduled, count: counts.scheduled },
    { id: "live", ...STAGE_META.live, count: counts.live },
    { id: "closed", ...STAGE_META.closed, count: counts.closed },
  ]
  if (counts.published > 0) {
    stages.push({ id: "published", ...STAGE_META.published, count: counts.published })
  }
  return stages.filter(stage => stage.count > 0)
}

interface EvaluationStagePipelineProps {
  counts: EvaluationCounts
  courseOfferings: number
  className?: string
  /** Persistent color dot + label + count row under the bar, not just on hover. */
  showLegend?: boolean
  /** Stage currently cross-filtering a companion table, if any. */
  activeStageId?: string | null
  /** Called when a clickable segment/legend entry is toggled. Omit to keep the bar passive. */
  onStageSelect?: (stageId: string) => void
  /** Stages with a matching row to filter to. Segments outside this set render but aren't clickable. */
  selectableStageIds?: ReadonlySet<string>
}

/**
 * Cross-stage distribution bar. Only renders when a term has courses split
 * across 2+ stages — a single-stage term's count and action already live in
 * the work-queue table row below, so a 100% bar here would just restate it.
 */
export function EvaluationStagePipeline({
  counts,
  courseOfferings,
  className,
  showLegend = false,
  activeStageId = null,
  onStageSelect,
  selectableStageIds,
}: EvaluationStagePipelineProps) {
  const stages = React.useMemo(() => buildStages(counts), [counts])
  const total = Math.max(
    courseOfferings,
    stages.reduce((sum, stage) => sum + stage.count, 0),
    1,
  )

  if (stages.length < 2) {
    return null
  }

  const summary = stages.map(stage => `${stage.count} ${stage.label.toLowerCase()}`).join(", ")
  const isInteractive = Boolean(onStageSelect)

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div
        className="flex h-3 w-full gap-0.5"
        role={isInteractive ? "group" : "img"}
        aria-label={summary}
      >
        {stages.map((stage, index) => {
          const isActive = activeStageId === stage.id
          const isSelectable = Boolean(onStageSelect) && (!selectableStageIds || selectableStageIds.has(stage.id))
          const dimmed = activeStageId != null && !isActive
          const segmentClassName = cn(
            "min-w-0 transition-opacity",
            index === 0 && "rounded-l-full",
            index === stages.length - 1 && "rounded-r-full",
            dimmed && "opacity-35",
          )
          const segmentStyle = { flexGrow: stage.count, flexBasis: 0, backgroundColor: stage.colorVar }

          return (
            <Tooltip key={stage.id}>
              <TooltipTrigger asChild>
                {isSelectable ? (
                  <button
                    type="button"
                    aria-pressed={isActive}
                    aria-label={`${stage.label}, ${stage.count} courses`}
                    className={cn(
                      segmentClassName,
                      "cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                    )}
                    style={segmentStyle}
                    onClick={() => onStageSelect?.(stage.id)}
                  />
                ) : (
                  <div className={segmentClassName} style={segmentStyle} />
                )}
              </TooltipTrigger>
              <TooltipContent side="top">
                {stage.count} {stage.label} · {Math.round((stage.count / total) * 100)}%
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
      {showLegend ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {stages.map(stage => {
            const isActive = activeStageId === stage.id
            const isSelectable = Boolean(onStageSelect) && (!selectableStageIds || selectableStageIds.has(stage.id))
            const dimmed = activeStageId != null && !isActive
            const entryClassName = cn(
              "inline-flex items-center gap-1.5 rounded-full text-sm text-muted-foreground transition-opacity",
              dimmed && "opacity-50",
            )
            const entry = (
              <>
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: stage.colorVar }}
                  aria-hidden="true"
                />
                <span className="text-foreground">{stage.count}</span> {stage.label}
              </>
            )

            if (!isSelectable) {
              return (
                <span key={stage.id} className={entryClassName}>
                  {entry}
                </span>
              )
            }

            return (
              <button
                key={stage.id}
                type="button"
                aria-pressed={isActive}
                onClick={() => onStageSelect?.(stage.id)}
                className={cn(
                  entryClassName,
                  "cursor-pointer outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                  isActive && "text-foreground",
                )}
              >
                {entry}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

interface ResponseTargetBarProps {
  rate: number
  target?: number
  closingLabel?: string
  className?: string
}

/** Live-window response fill against the institutional target. */
export function ResponseTargetBar({
  rate,
  target = 70,
  closingLabel,
  className,
}: ResponseTargetBarProps) {
  const fill = Math.max(0, Math.min(100, rate))

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium text-foreground">Live response</span>
        <span className="tabular-nums text-muted-foreground">
          {rate}% · target {target}%
          {closingLabel ? ` · ${closingLabel}` : ""}
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={fill}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Live response ${rate} percent`}
      >
        <div
          className={cn(
            "h-full rounded-full transition-all",
            rate >= target ? "bg-chart-2" : rate >= 50 ? "bg-chart-4" : "bg-destructive",
          )}
          style={{ width: `${fill}%` }}
        />
      </div>
    </div>
  )
}

type LabeledLifecycleStageId = keyof EvaluationCounts

const LABELED_LIFECYCLE_STAGES: {
  id: LabeledLifecycleStageId
  label: string
  toneClass: string
  phase: "setup" | "collection" | "results"
}[] = [
  { id: "notSetup", label: "Not set up", toneClass: STATUS_BADGE_TONE_CLASS.danger, phase: "setup" },
  { id: "draft", label: "Draft", toneClass: STATUS_BADGE_TONE_CLASS.warning, phase: "setup" },
  { id: "scheduled", label: "Scheduled", toneClass: STATUS_BADGE_TONE_CLASS.info, phase: "setup" },
  {
    id: "live",
    label: "Collecting",
    toneClass:
      "bg-[var(--icon-disc-brand-bg)] text-[var(--icon-disc-brand-fg)]",
    phase: "collection",
  },
  { id: "closed", label: "Closed", toneClass: STATUS_BADGE_TONE_CLASS.success, phase: "results" },
  { id: "published", label: "Published", toneClass: STATUS_BADGE_TONE_CLASS.neutral, phase: "results" },
]

const LIFECYCLE_PHASES: { id: "setup" | "collection" | "results"; label: string }[] = [
  { id: "setup", label: "Setup" },
  { id: "collection", label: "Collection" },
  { id: "results", label: "Results" },
]

function stageFlexGrow(count: number): number {
  return Math.max(count, 1)
}

/** Six-stage labeled mix for Operations V2. Width follows count. Zero published still shows. */
export function EvaluationLabeledLifecycle({
  counts,
  courseOfferings,
  stageHref,
  className,
}: {
  counts: EvaluationCounts
  courseOfferings: number
  stageHref: (stageId: LabeledLifecycleStageId) => string
  className?: string
}) {
  const stages = LABELED_LIFECYCLE_STAGES.map(stage => ({
    ...stage,
    count: counts[stage.id],
    grow: stageFlexGrow(counts[stage.id]),
  }))
  const phaseGrow: Record<(typeof LIFECYCLE_PHASES)[number]["id"], number> = {
    setup: 0,
    collection: 0,
    results: 0,
  }
  for (const stage of stages) {
    phaseGrow[stage.phase] += stage.grow
  }
  const total = Math.max(courseOfferings, 1)

  return (
    <div className={cn("overflow-x-auto", className)}>
      <div className="flex min-w-[40rem] flex-col gap-2">
        <ul className="flex gap-1">
          {stages.map(stage => {
            const percent = Math.round((stage.count / total) * 100)
            const href = stageHref(stage.id)
            const coursesOf =
              courseOfferings === 1
                ? `${stage.count} of 1 course`
                : `${stage.count} of ${courseOfferings} courses`

            return (
              <li
                key={stage.id}
                className="min-h-16 min-w-20"
                style={{ flexGrow: stage.grow, flexBasis: 0 }}
              >
                <HoverCard openDelay={200} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <Link
                      to={href}
                      aria-label={`${stage.label}. ${coursesOf}. ${percent}%`}
                      className={cn(
                        "flex h-full min-h-16 flex-col justify-center gap-0.5 rounded-lg px-2.5 py-2 outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        stage.toneClass,
                      )}
                    >
                      <p className="text-lg font-medium tabular-nums">{stage.count}</p>
                      <p className="text-xs text-pretty opacity-80">{stage.label}</p>
                    </Link>
                  </HoverCardTrigger>
                  <HoverCardContent side="top" className="w-52">
                    <p className="font-medium text-foreground">{stage.label}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{coursesOf}</p>
                    <p className="text-sm tabular-nums text-muted-foreground">{percent}%</p>
                  </HoverCardContent>
                </HoverCard>
              </li>
            )
          })}
        </ul>
        <div className="flex gap-1">
          {LIFECYCLE_PHASES.map(phase => (
            <p
              key={phase.id}
              className="min-w-0 text-xs font-medium text-muted-foreground"
              style={{ flexGrow: phaseGrow[phase.id], flexBasis: 0 }}
            >
              {phase.label}
            </p>
          ))}
        </div>
      </div>
    </div>
  )
}

export { evaluationCoverageCount } from "@/lib/mock/evaluation-dashboard"
